import { useState } from 'react';
import DifficultySlider from './DifficultySlider';
import StoryModePanel from './StoryModePanel';
import TryItPanel from './TryItPanel';
import MicroInteractionCard from '../MicroInteractionCard';
import { getGradeBand } from '../../services/conceptPlus1Service';
import type { AssignmentContext } from '../../services/assignmentService';

type Tile = 'difficulty' | 'story' | 'simulate' | 'predict';

// Story mode isn't linked to any teacher-facing insight yet — hidden from
// students until it's wired into mastery reporting. Kept in place (not
// deleted) so it can be re-enabled without rebuilding it.
const STORY_MODE_ENABLED = false;

interface Props {
  conceptId: number;
  conceptName: string;
  gradeLevel: number;
  board: string;
  studentId: string;
  subjectId?: number;
  subjectName: string;
  realLifeExample?: string | null;
  explanationBasic?: string | null;
  explanationStandard?: string | null;
  explanationAdvanced?: string | null;
  assignmentContext?: AssignmentContext;
  onMicroComplete?: () => void;
  onSimulateComplete?: () => void;
  onAwardBadge?: (criteria: string) => void;
}

const TILES: { key: Tile; label: string; icon: string }[] = [
  ...(STORY_MODE_ENABLED ? [{ key: 'story' as const, label: 'Story Mode', icon: '📖' }] : []),
  { key: 'predict',    label: 'Predict',         icon: '🔮' },
  { key: 'simulate',   label: 'Simulate',        icon: '🧪' },
  { key: 'difficulty', label: 'Difficulty Level', icon: '🎯' },
];

export default function ExplorePanel({
  conceptId, conceptName, gradeLevel, studentId,
  subjectName, realLifeExample, explanationBasic, explanationStandard, explanationAdvanced,
  assignmentContext, onMicroComplete, onSimulateComplete, onAwardBadge,
}: Props) {
  const [activeTile, setActiveTile] = useState<Tile>(STORY_MODE_ENABLED ? 'story' : 'predict');

  // Simulate takes over the full viewport so the student has no distractions
  if (activeTile === 'simulate') {
    return (
      <div className="fixed inset-0 z-50 flex flex-col bg-white">
        {/* Header strip */}
        <div className="flex-none flex items-center justify-between px-4 h-12 border-b border-gray-100 bg-white">
          <button
            onClick={() => setActiveTile(STORY_MODE_ENABLED ? 'story' : 'predict')}
            className="flex items-center gap-1 text-sm font-medium text-gray-500 hover:text-gray-800 transition-colors"
          >
            ← Back
          </button>
          <span className="text-sm font-semibold text-gray-700 truncate max-w-[60%] text-center">
            🧪 {conceptName}
          </span>
          <div className="w-16" />
        </div>

        {/* TryItPanel fills everything below the header */}
        <div className="flex-1 overflow-hidden flex flex-col">
          <TryItPanel
            conceptId={conceptId}
            conceptName={conceptName}
            subjectName={subjectName}
            gradeLevel={gradeLevel}
            studentId={studentId}
            onComplete={() => {
              setActiveTile(STORY_MODE_ENABLED ? 'story' : 'predict');
              onSimulateComplete?.();
              onAwardBadge?.('tryit_complete');
            }}
            fullscreen
            assignmentContext={assignmentContext}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
      {/* Header */}
      <div className="px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-indigo-50 to-teal-50">
        <h2 className="text-base font-bold text-gray-800">Explore</h2>
        <p className="text-xs text-gray-500 mt-0.5">Interactive ways to learn {conceptName}</p>
      </div>

      {/* Tile nav */}
      <div className="flex overflow-x-auto border-b border-gray-100 bg-gray-50 scrollbar-none">
        {TILES.map(({ key, label, icon }) => (
          <button
            key={key}
            onClick={() => setActiveTile(key)}
            className={`flex-shrink-0 flex items-center gap-1.5 px-4 py-3 text-xs font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTile === key
                ? 'border-coral-warm text-coral-warm bg-white'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-100'
            }`}
          >
            <span className="text-sm">{icon}</span>
            {label}
          </button>
        ))}
      </div>

      {/* Active tile content */}
      <div className="p-5">
        {STORY_MODE_ENABLED && activeTile === 'story' && (
          <StoryModePanel
            conceptName={conceptName}
            gradeLevel={gradeLevel}
            studentId={studentId}
          />
        )}

        {activeTile === 'predict' && (
          realLifeExample ? (
            <MicroInteractionCard
              conceptId={conceptId}
              conceptName={conceptName}
              subject={subjectName}
              mode="real_life"
              contentText={realLifeExample}
              gradeBand={getGradeBand(gradeLevel)}
              accentColor="green"
              studentId={studentId}
              onComplete={onMicroComplete}
              onAwardBadge={onAwardBadge}
            />
          ) : (
            <p className="text-sm text-gray-400 text-center py-6">
              No real-life scenario available for this concept yet.
            </p>
          )
        )}

        {activeTile === 'difficulty' && (
          <DifficultySlider
            conceptName={conceptName}
            gradeLevel={gradeLevel}
            studentId={studentId}
            explanationBasic={explanationBasic}
            explanationStandard={explanationStandard}
            explanationAdvanced={explanationAdvanced}
          />
        )}
      </div>
    </div>
  );
}
